// BusinessApiService.ts - CORRECTED TO MATCH YOUR ACTUAL BACKEND ROUTES
import axios, { AxiosInstance } from "axios";

// =====================================================
// TYPES THAT MATCH YOUR BACKEND EXACTLY
// =====================================================
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  code?: string;
}

// =====================
// SUPPLY CHAIN TYPES
// =====================
export interface Shipment {
  shipmentId?: string;
  trackingNumber?: string;
  batchId: string;
  productId?: string;
  status:
    | "assigned"
    | "label_generated"
    | "ready_to_ship"
    | "in_transit"
    | "delivered"
    | "received"
    | "exception";
  currentLocation?: string;
  carrierName?: string;
  eta?: string;
  history?: Array<{
    status: Shipment["status"] | string;
    timestamp: string; // ISO
    location?: string;
    note?: string;
  }>;
}

export interface TrackResponse {
  trackingNumber: string;
  status: Shipment["status"] | string;
  currentLocation?: string;
  eta?: string;
  history?: Shipment["history"];
  batch?: any;
  product?: any;
}

// EXACT registration payload your backend expects (from business.js)
interface BusinessRegistrationPayload {
  business: {
    companyName: string;
    tradingName?: string;
    registrationNumber?: string;
    taxId?: string;
    industry: string;
    website?: string;
    phone?: string;
    email: string;
    address?: {
      street?: string;
      city?: string;
      state?: string;
      postalCode?: string;
      country?: string;
    };
    contactPerson?: {
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
      position?: string;
    };
  };
  businessUser: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    position?: string;
    role?: string;
    isAdmin?: boolean;
    userType?: string;
  };
  agreeToTerms: boolean;
}

// Response types from your backend
interface RegisterBusinessResponse {
  businessId: string;
  userId: string;
  business: any;
  user: any;
  token: string;
  refreshToken?: string;
  nextSteps?: string[];
  redirect?: string;
}

interface LoginBusinessResponse {
  user: any;
  business: any;
  token: string;
  refreshToken?: string;
  redirect?: string;
}

interface CheckAvailabilityResponse {
  available: {
    businessEmail?: boolean;
    companyName?: boolean;
    adminEmail?: boolean;
  };
}

// =====================================================
// HELPER FUNCTIONS
// =====================================================
const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

function asErr(e: any, fallback = "Request failed"): string {
  return e?.response?.data?.message || e?.message || fallback;
}


// =====================================================
// CORRECTED API SERVICE CLASS
// =====================================================
class BusinessApiService {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_BASE,
      timeout: 10000,
      withCredentials: false,
      headers: { "Content-Type": "application/json" },
    });

    // Add auth token to requests
    this.axiosInstance.interceptors.request.use((config) => {
      const token = this.getBusinessToken();
      if (token) {
        config.headers = config.headers ?? {};
        (config.headers as any).Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle auth errors
    this.axiosInstance.interceptors.response.use(
      (res) => res,
      (error) => {
        if (error?.response?.status === 401) {
          this.clearBusinessData();
          window.location.href = "/business/login";
        }
        return Promise.reject(error);
      }
    );
  }

  // =====================================================
  // PUBLIC ENDPOINTS - CORRECTED TO MATCH YOUR BACKEND
  // =====================================================

  // MAIN REGISTRATION - Uses /api/business/register (from your business.js)
  async registerBusiness(registrationData: BusinessRegistrationPayload): Promise<ApiResponse<RegisterBusinessResponse>> {
    try {
      console.log('🚀 Calling /api/business/register...');
      console.log('📤 Registration payload:', registrationData);
      
      const res = await this.axiosInstance.post('/business/register', registrationData);
      console.log('📨 Registration response:', res.data);
      
      // Auto-store token and user data if successful
      if (res.data.success && res.data.data) {
        if (res.data.data.token) {
          this.setBusinessToken(res.data.data.token);
        }
        if (res.data.data.refreshToken) {
          localStorage.setItem("refreshToken", res.data.data.refreshToken);
        }
        if (res.data.data.user) {
          this.setBusinessData(res.data.data.user);
        }
        if (res.data.data.business) {
          localStorage.setItem("business", JSON.stringify(res.data.data.business));
        }
      }
      
      return res.data;
    } catch (e: any) {
      console.error('❌ Registration error:', e);
      throw new Error(asErr(e, "Business registration failed"));
    }
  }

  // LOGIN - Uses /api/business/login (from your business.js)
  async loginBusinessUser(credentials: {
    email: string;
    password: string;
    rememberMe?: boolean;
  }): Promise<ApiResponse<LoginBusinessResponse>> {
    try {
      console.log('🔐 Calling /api/business/login...');
      
      const cleanCredentials = {
        email: credentials.email,
        password: credentials.password,
        ...(credentials.rememberMe !== undefined && { rememberMe: credentials.rememberMe })
      };
      
      console.log('📤 Login credentials:', { email: cleanCredentials.email, rememberMe: cleanCredentials.rememberMe });
      
      const res = await this.axiosInstance.post('/business/login', cleanCredentials);
      console.log('📨 Login response:', res.data);
      
      // Auto-store token and user data if successful
      if (res.data.success && res.data.data) {
        if (res.data.data.token) {
          this.setBusinessToken(res.data.data.token);
        }
        if (res.data.data.refreshToken) {
          localStorage.setItem("refreshToken", res.data.data.refreshToken);
        }
        if (res.data.data.user) {
          this.setBusinessData(res.data.data.user);
        }
        if (res.data.data.business) {
          localStorage.setItem("business", JSON.stringify(res.data.data.business));
        }
      }
      
      return res.data;
    } catch (e: any) {
      console.error('❌ Login error:', e);
      throw new Error(asErr(e, "Login failed"));
    }
  }

  // AVAILABILITY CHECK - Uses /api/business/check-availability
  async checkAvailability(data: {
    email?: string;
    companyName?: string;
    adminEmail?: string;
  }): Promise<ApiResponse<CheckAvailabilityResponse>> {
    try {
      console.log('🔍 Checking availability...');
      const res = await this.axiosInstance.post('/business/check-availability', data);
      return res.data;
    } catch (e: any) {
      console.error('❌ Availability check error:', e);
      return { 
        success: false, 
        data: { available: {} }, 
        message: asErr(e, "Availability check failed") 
      };
    }
  }

  // AUTH LOGIN - Uses /api/auth/business-user/login (from your auth.js)
  async authLogin(credentials: {
    email: string;
    password: string;
    rememberMe?: boolean;
  }): Promise<ApiResponse<any>> {
    try {
      console.log('🔐 Calling /api/auth/business-user/login...');
      
      const cleanCredentials = {
        email: credentials.email,
        password: credentials.password,
        ...(credentials.rememberMe !== undefined && { rememberMe: credentials.rememberMe })
      };
      
      //const res = await this.axiosInstance.post('/auth/business-user/login', cleanCredentials);
      const res = await this.axiosInstance.post('/business-user/login', cleanCredentials);

      console.log('📨 Auth login response:', res.data);
      
      return res.data;
    } catch (e: any) {
      console.error('❌ Auth login error:', e);
      throw new Error(asErr(e, "Auth login failed"));
    }
  }
// =====================
// SUPPLY CHAIN API
// =====================

// Assign a batch to the next party (e.g., distributor or shipper)
async scAssignBatch(batchId: string, payload: {
  assignedToType: "distributor" | "shipper" | "retailer" | string;
  assignedToId: string;
  assignedToName?: string;
}): Promise<ApiResponse<{ batch: any }>> {
  try {
    const res = await this.axiosInstance.post(`/supply-chain/batch/${encodeURIComponent(batchId)}/assign`, payload);
    return res.data;
  } catch (e: any) {
    throw new Error(this.handleApiError(e) || "Failed to assign batch");
  }
}

// Generate tracking label (shipper scans the batch/master)
async scGenerateTracking(payload: {
  batchId: string;
  shipperId: string;        // current business id
  carrierName?: string;
}): Promise<ApiResponse<{ trackingNumber: string; shipmentId: string; batch?: any }>> {
  try {
    const res = await this.axiosInstance.post(`/supply-chain/shipper/scan-generate-tracking`, payload);
    return res.data;
  } catch (e: any) {
    throw new Error(this.handleApiError(e) || "Failed to create tracking");
  }
}

// Mark Ready to Ship
async scReadyToShip(payload: {
  batchId: string;
  shipperId: string;
}): Promise<ApiResponse<{ shipment?: Shipment; batch?: any }>> {
  try {
    const res = await this.axiosInstance.post(`/supply-chain/shipper/scan-ready-to-ship`, payload);
    return res.data;
  } catch (e: any) {
    throw new Error(this.handleApiError(e) || "Failed to mark Ready to Ship");
  }
}

// Update in-transit location (optional)
async scUpdateLocation(trackingId: string, payload: {
  location: string;
  status?: Shipment["status"] | "in_transit";
  timestamp?: string; // ISO
}): Promise<ApiResponse<{ shipment?: Shipment }>> {
  try {
    const res = await this.axiosInstance.post(`/supply-chain/update-location/${encodeURIComponent(trackingId)}`, payload);
    return res.data;
  } catch (e: any) {
    throw new Error(this.handleApiError(e) || "Failed to update location");
  }
}

// Receiver marks as received
async scMarkReceived(payload: {
  batchId: string;
  receiverId: string;
  receiverName?: string;
  receivedLocation?: string;
}): Promise<ApiResponse<{ shipment?: Shipment; batch?: any }>> {
  try {
    const res = await this.axiosInstance.post(`/supply-chain/receiver/scan-received`, payload);
    return res.data;
  } catch (e: any) {
    throw new Error(this.handleApiError(e) || "Failed to mark received");
  }
}

// Track a shipment by tracking id/number
async scTrack(trackingId: string): Promise<ApiResponse<TrackResponse>> {
  try {
    const res = await this.axiosInstance.get(`/supply-chain/track/${encodeURIComponent(trackingId)}`);
    return res.data;
  } catch (e: any) {
    throw new Error(this.handleApiError(e) || "Failed to track shipment");
  }
}

// Get all shipments for a batch (if exposed by backend)
async scGetShipmentsByBatch(batchId: string): Promise<ApiResponse<{ shipments: Shipment[] }>> {
  try {
    const res = await this.axiosInstance.get(`/supply-chain/${encodeURIComponent(batchId)}`);
    return res.data;
  } catch (e: any) {
    throw new Error(this.handleApiError(e) || "Failed to load shipments for batch");
  }
}

// Analytics (optional)
async scAnalytics(businessId: string): Promise<ApiResponse<any>> {
  try {
    const res = await this.axiosInstance.get(`/supply-chain/analytics/${encodeURIComponent(businessId)}`);
    return res.data;
  } catch (e: any) {
    throw new Error(this.handleApiError(e) || "Failed to load supply chain analytics");
  }
}

// Helper to read current business id (optional)
getCurrentBusinessId(): string | null {
  try {
    const b = JSON.parse(localStorage.getItem("business") || "null");
    return b?._id || b?.businessId || null;
  } catch {
    return null;
  }
}

  // =====================================================
  // FIXED: FORGOT PASSWORD - Uses /api/auth/business-user/forgot-password
  // =====================================================
  async forgotPassword(email: string): Promise<ApiResponse<{ message: string }>> {
    try {
      console.log('📧 Calling auth forgot password endpoint...');
      console.log('📤 Email:', email);
      
      // Call the auth endpoint that already exists and works
      const res = await this.axiosInstance.post('/auth/business-user/forgot-password', { email });
      
      console.log('📨 Auth forgot password response:', res.data);
      return res.data;
    } catch (e: any) {
      console.error('❌ Forgot password error:', e);
      return { 
        success: false, 
        data: { message: '' }, 
        message: this.handleApiError(e) || "Password reset failed"
      };
    }
  }

  // RESET PASSWORD - Uses /api/auth/business-user/reset-password
  async resetPassword(token: string, password: string): Promise<ApiResponse<any>> {
    try {
      console.log('🔐 Calling auth reset password endpoint...');
      console.log('📤 Token provided:', !!token);
      
      // Call the auth endpoint
      const res = await this.axiosInstance.post('/auth/business-user/reset-password', { 
        token, 
        password 
      });
      
      console.log('📨 Auth reset password response:', res.data);
      return res.data;
    } catch (e: any) {
      console.error('❌ Reset password error:', e);
      return { 
        success: false, 
        data: null as any, 
        message: this.handleApiError(e) || "Password reset failed"
      };
    }
  }

  // GET PROFILE - Uses /api/business-user/profile (from your business-user.js)
  async getProfile(): Promise<ApiResponse<{ user: any }>> {
    try {
      const res = await this.axiosInstance.get('/business-user/profile');
      return res.data;
    } catch (e: any) {
      // Fallback to cached data
      const cached = this.getCurrentBusiness();
      if (cached) {
        return { 
          success: true, 
          data: { user: cached }, 
          message: "Using cached profile data" 
        };
      }
      throw new Error(asErr(e, "Failed to get profile"));
    }
  }

  // UPDATE PROFILE - Uses /api/business-user/profile
  async updateProfile(profileData: {
    firstName?: string;
    lastName?: string;
    position?: string;
    department?: string;
    phone?: string;
    timezone?: string;
    language?: string;
  }): Promise<ApiResponse<{ user: any }>> {
    try {
      const res = await this.axiosInstance.put('/business-user/profile', profileData);
      
      // Update cached data
      if (res.data?.success && res.data?.data?.user) {
        this.setBusinessData(res.data.data.user);
      }
      
      return res.data;
    } catch (e: any) {
      throw new Error(asErr(e, "Failed to update profile"));
    }
  }

  // GET DASHBOARD - Uses /api/business-user/dashboard
  async getDashboard(): Promise<ApiResponse<{ dashboard: any }>> {
    try {
      const res = await this.axiosInstance.get('/business-user/dashboard');
      return res.data;
    } catch (e: any) {
      // Fallback to mock data
      console.warn('⚠️ Dashboard endpoint failed, using mock data');
      return {
        success: true,
        data: {
          dashboard: {
            user: this.getCurrentBusiness(),
            stats: { 
              totalScans: 1247, 
              uniqueVisitors: 892, 
              conversionRate: 18.5,
              activeTags: 89,
              activeCampaigns: 3,
              teamMembers: 5
            },
            recentActivity: [],
            notifications: [],
          }
        },
        message: "Using mock dashboard data",
      };
    }
  }

  // =====================================================
  // BUSINESS PROFILE ENDPOINTS
  // =====================================================

  // GET BUSINESS PROFILE - Uses /api/business/profile
  async getBusinessProfile(): Promise<ApiResponse<{ business: any }>> {
    try {
      const res = await this.axiosInstance.get('/business/profile');
      return res.data;
    } catch (e: any) {
      throw new Error(asErr(e, "Failed to get business profile"));
    }
  }

  // UPDATE BUSINESS PROFILE - Uses /api/business/profile
  async updateBusinessProfile(businessData: {
    companyName?: string;
    website?: string;
    phone?: string;
    description?: string;
    address?: any;
  }): Promise<ApiResponse<{ business: any }>> {
    try {
      const res = await this.axiosInstance.put('/business/profile', businessData);
      return res.data;
    } catch (e: any) {
      throw new Error(asErr(e, "Failed to update business profile"));
    }
  }

  // =====================================================
  // TEST ENDPOINTS
  // =====================================================

  // Test email functionality - Uses /api/business/test-email
  async testEmail(): Promise<ApiResponse<any>> {
    try {
      const res = await this.axiosInstance.get('/business/test-email');
      return res.data;
    } catch (e: any) {
      throw new Error(asErr(e, "Email test failed"));
    }
  }

  // Test business routes - Uses /api/business/test
  async testBusinessRoutes(): Promise<ApiResponse<any>> {
    try {
      const res = await this.axiosInstance.get('/business/test');
      return res.data;
    } catch (e: any) {
      throw new Error(asErr(e, "Business routes test failed"));
    }
  }

  // Get test data - Uses /api/business/test-data
  async getTestData(): Promise<ApiResponse<any>> {
    try {
      const res = await this.axiosInstance.get('/business/test-data');
      return res.data;
    } catch (e: any) {
      throw new Error(asErr(e, "Failed to get test data"));
    }
  }

  // =====================================================
  // LOGOUT
  // =====================================================
  async logout(): Promise<void> {
    try {
      console.log('🚪 Calling logout endpoint...');
      await this.axiosInstance.post('/business/logout');
    } catch (e) {
      console.warn('⚠️ Logout endpoint failed, clearing local data anyway');
    } finally {
      this.clearBusinessData();
    }
  }

  // =====================================================
  // LOCAL STORAGE MANAGEMENT
  // =====================================================
  getBusinessToken(): string | null {
    return (
      localStorage.getItem("authToken") ||
      localStorage.getItem("businessToken") ||
      localStorage.getItem("auth_token") ||
      localStorage.getItem("token") ||
      null
    );
  }

  setBusinessToken(token: string): void {
    localStorage.setItem("authToken", token);
    localStorage.setItem("businessToken", token);
  }

  getCurrentBusiness(): any | null {
    try {
      const raw =
        localStorage.getItem("businessUser") ||
        localStorage.getItem("businessData") ||
        localStorage.getItem("business") ||
        localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  setBusinessData(business: any): void {
    localStorage.setItem("businessUser", JSON.stringify(business));
    localStorage.setItem("businessData", JSON.stringify(business));
    localStorage.setItem("user", JSON.stringify(business));
    localStorage.setItem("userType", "business_user");
  }

  clearBusinessData(): void {
    [
      "businessToken", "authToken", "auth_token", "token", 
      "business", "businessData", "businessUser", "user", 
      "userType", "refreshToken", "businessRegistration"
    ].forEach((k) => localStorage.removeItem(k));
    sessionStorage.clear();
  }

  isLoggedIn(): boolean {
    const token = this.getBusinessToken();
    const user = this.getCurrentBusiness();
    const userType = localStorage.getItem("userType");
    
    return !!(token && user && userType === "business_user");
  }

  // =====================================================
  // HELPER METHODS
  // =====================================================
  handleApiError(error: any): string {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    
    if (error.response?.data?.error) {
      return error.response.data.error;
    }
    
    if (error.message) {
      return error.message;
    }
    
    return 'An unexpected error occurred. Please try again.';
  }

  // Get business status
  getBusinessStatus(): string | null {
    const businessData = localStorage.getItem("business");
    if (!businessData) return null;
    
    try {
      const business = JSON.parse(businessData);
      return business.status || null;
    } catch {
      return null;
    }
  }

  // Check if business is approved
  isBusinessApproved(): boolean {
    return this.getBusinessStatus() === 'approved';
  }

  // =====================================================
  // DEBUG METHODS
  // =====================================================
  getConfig() {
    return {
      baseURL: this.axiosInstance.defaults.baseURL,
      frontendURL: window.location.origin,
      environment: process.env.NODE_ENV,
      isLoggedIn: this.isLoggedIn(),
      currentBusiness: this.getCurrentBusiness(),
      backendEndpoints: {
        // Business endpoints
        registration: '/business/register', // ✅ From business.js
        businessLogin: '/business/login',   // ✅ From business.js
        checkAvailability: '/business/check-availability',
        businessProfile: '/business/profile',
        logout: '/business/logout',
        testEmail: '/business/test-email',
        testRoutes: '/business/test',
        testData: '/business/test-data',
        
        // Auth endpoints (CORRECTED)
        authLogin: '/auth/business-user/login', // ✅ From auth.js
        forgotPassword: '/auth/business-user/forgot-password', // ✅ FIXED
        resetPassword: '/auth/business-user/reset-password',   // ✅ FIXED
        
        // Business User endpoints
        userProfile: '/business-user/profile', // ✅ From business-user.js
        dashboard: '/business-user/dashboard'  // ✅ From business-user.js
      }
    };
  }

  // =====================================================
  // CONVENIENCE METHODS FOR FRONTEND COMPATIBILITY
  // =====================================================

  // Helper method to convert frontend form data to backend format
  convertFormDataToRegistrationPayload(formData: any): BusinessRegistrationPayload {
    return {
      business: {
        companyName: formData.companyName,
        tradingName: formData.tradingName || undefined,
        registrationNumber: formData.registrationNumber || undefined,
        taxId: formData.taxId || undefined,
        industry: formData.industry,
        website: formData.website || undefined,
        phone: formData.phone || '',
        email: formData.email,
        address: {
          street: formData.street || '',
          city: formData.city || '',
          state: formData.state || '',
          postalCode: formData.postalCode || '',
          country: formData.country || 'US'
        },
        contactPerson: {
          firstName: formData.contactFirstName || formData.adminFirstName,
          lastName: formData.contactLastName || formData.adminLastName,
          email: formData.contactEmail || formData.email,
          phone: formData.contactPhone || formData.phone || '',
          position: formData.position || formData.adminPosition || 'Owner'
        }
      },
      businessUser: {
        firstName: formData.adminFirstName,
        lastName: formData.adminLastName,
        email: formData.adminEmail,
        password: formData.password,
        position: formData.adminPosition || 'Owner',
        role: 'business_owner',
        isAdmin: true,
        userType: 'business_user'
      },
      agreeToTerms: formData.agreeToTerms || true
    };
  }

  // Legacy method for compatibility
  async registerBusinessLegacy(formData: any): Promise<ApiResponse<any>> {
    const payload = this.convertFormDataToRegistrationPayload(formData);
    return this.registerBusiness(payload);
  }

  // =====================================================
  // ALTERNATIVE METHODS (if you want both options)
  // =====================================================

  // Alternative forgot password using auth endpoint (already implemented above)
  async forgotPasswordAuth(email: string): Promise<ApiResponse<any>> {
    return this.forgotPassword(email); // Uses the corrected method above
  }

  // Alternative forgot password using business endpoint (if you add the route)
  async forgotPasswordBusiness(email: string): Promise<ApiResponse<any>> {
    try {
      console.log('📧 Calling business forgot password endpoint...');
      const res = await this.axiosInstance.post('/business/forgot-password', { email });
      return res.data;
    } catch (e: any) {
      return { 
        success: false, 
        data: { message: '' }, 
        message: this.handleApiError(e) || "Password reset failed" 
      };
    }
  }
}

// =====================================================
// CREATE AND EXPORT SINGLETON INSTANCE
// =====================================================
const BusinessApiServiceInstance = new BusinessApiService();
export default BusinessApiServiceInstance;